import { useEffect, useState } from "react";
import { isAdminRole } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { getOptionCategory } from "../../data/lookups";
import { getShelterCustomerType } from "../../data/shelters";
import { hasApprovedDispatchItem } from "../../services/deliveryApprovalHold";
import { C } from "../../utils/constants";
import { money } from "../../utils/helpers";
import { formatPhone } from "../../utils/sanitize";
import { ShelterInfoBox } from "./RequestDetailPanels.jsx";
import { RequestPeopleModal } from "./RequestPeopleModal.jsx";
import { VerifyAddressModal } from "./VerifyAddressModal.jsx";

// ⚠️ CROSS-REFERENCE: the delivery block below (address + verify / ban /
// unregistered warnings + Edit Address) is duplicated in
// src/components/request/TopUpTopInfoStrip.jsx for the Top-up approval view (kept
// separate on purpose so top-up changes can't touch this live responder view). If
// you change the warning logic here, check that file too — and vice-versa.
export function StaffTopInfoStrip({
  req,
  shelterRegAddrs,
  shelterAddrsLoaded,
  setEditDelivery,
  setShowAddressModal,
  onVerifyAddress,
  totals,
  // Possible-repeat flag (db-210): { count, strong, childIndexes } from useRepeatCheck.
  repeatSummary = null,
}) {
  const { user } = useSession();
  const { feeTotal, slTotal, uniTotal, shoeTotal, laptopTotal, hasLaptops, grand } = totals;
  const [showPeople, setShowPeople] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  // The DFV Service's broad Customer-Type category (db-124), shown top-right of the
  // General Information card. Resolved LIVE from the service (its current
  // customer_type → the value's option_category), so it works on any request — old
  // or new — without storing anything on the request itself.
  // NB: this is a "live side-read" — a service attribute shown on a request that
  // requests_view does NOT carry. See the LIVE SIDE-READS register above
  // REQUEST_COLS in src/data/requests.ts; if more of these appear, fold them into
  // requests_view instead of adding more per-screen fetches.
  const [customerCategory, setCustomerCategory] = useState("");
  useEffect(
    function () {
      let alive = true;
      if (!req.shelterCode) {
        setCustomerCategory("");
        return;
      }
      getShelterCustomerType(req.shelterCode)
        .then(function (customerType) {
          if (!customerType) return null;
          return getOptionCategory("customer_type", customerType);
        })
        .then(function (cat) {
          if (alive) setCustomerCategory(cat || "");
        });
      return function () {
        alive = false;
      };
    },
    [req.shelterCode]
  );
  // A responder can verify a shelter-entered one-off address from here (self-unblocks
  // the Device / Stationery-Pack approval hold). Shown only for an unverified, linked
  // address and only when the caller can verify (onVerifyAddress supplied for the
  // responder job — pickers don't get it).
  const canVerifyAddress =
    req.deliveryAddressVerified === false && !!req.deliveryAddressId && !!onVerifyAddress;
  // Once a Stationery Pack or Device is APPROVED, the delivery address + DFV Service
  // notes are locked for Responders / Pickers / Combined (an approved dispatch item
  // has committed an SO to that address) — but a Supervisor+ (admin/superuser) can
  // still edit (John, 31 Jul 2026).
  const lockEdits = hasApprovedDispatchItem(req.children) && !isAdminRole(user);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 12,
        marginBottom: 14,
        alignItems: "stretch",
      }}
    >
      {/* General info */}
      <div
        style={{
          marginBottom: 0,
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: C.blue,
            color: "white",
            padding: "0 18px",
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          <span>General Information</span>
          {customerCategory && (
            <span
              title="DFV Service Type category"
              style={{
                fontSize: 12,
                fontWeight: 600,
                background: "#E3F2FD",
                color: "#37474F",
                border: "1px solid #CFE0DE",
                borderRadius: 6,
                padding: "3px 10px",
                whiteSpace: "nowrap",
              }}
            >
              {customerCategory}
            </span>
          )}
        </div>
        <div
          style={{
            padding: "14px 16px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {[
            ["DFV Service Ref", req.shelterRef],
            ["DFV Service", req.shelterName],
            ["Contact Name", req.contactName],
            ["Contact Email", req.shelterEmail],
            // Mobile on the LHS, Phone on the RHS (space-between). A lone number
            // sits on the left (single flex child aligns to the start), so a
            // request that only captured one number isn't floated to the right.
            [
              "Mobile / Phone",
              req.mobile || req.phone ? (
                <div
                  key="nums"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 14,
                  }}
                >
                  {req.mobile ? <span>{formatPhone(req.mobile)}</span> : null}
                  {req.phone ? <span>{formatPhone(req.phone)}</span> : null}
                </div>
              ) : null,
            ],
          ].map(function (pair) {
            const l = pair[0];
            const v = pair[1];
            return v ? (
              <div
                key={l}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0,
                  marginBottom: 6,
                  paddingBottom: 6,
                  borderBottom: `1px solid ${"#CFE0DE"}`,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#607D79",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    width: 120,
                    flexShrink: 0,
                  }}
                >
                  {l}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1A2E2B", flex: 1 }}>{v}</div>
              </div>
            ) : null;
          })}
          {/* Delivery: label ABOVE the content so the address / Verify button /
              pending message use the FULL card width (John), not the narrow value
              column the other rows sit in. */}
          <div style={{ marginTop: 4 }}>
            {/* Delivery: label + address share ONE row (like the other fields) to save
                vertical space; the warnings + Edit button stay full-width below. */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 0, marginBottom: 6 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#607D79",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  width: 120,
                  flexShrink: 0,
                }}
              >
                Delivery
              </div>
              <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5, flex: 1 }}>
                {req.deliveryAddress}
              </div>
            </div>
            <div>
              {req.deliveryAddressBanned === true && (
                // Promotion model (db-147): the linked delivery address is BANNED
                // (do-not-use). This hard-blocks approving a Device / Stationery Pack.
                // No verify path — resolve by re-routing (Edit Address) or a
                // supervisor removing the ban. Takes priority over the pending message.
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#fff",
                    background: "#B71C1C",
                    border: "1px solid #7f1010",
                    borderRadius: 5,
                    padding: "5px 9px",
                    marginBottom: 6,
                  }}
                >
                  ⛔ Delivery address BANNED — do not use. Change the address (Edit Address) or ask
                  a supervisor to remove the ban.
                </div>
              )}
              {req.deliveryAddressVerified === false && req.deliveryAddressBanned !== true && (
                // Verified-address model (db-100): the linked delivery address is a
                // shelter-entered one-off awaiting a responder's verification. The
                // Verify button (when the caller can verify) sits on the SAME row, to
                // the LEFT of the message, to save vertical space.
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  {canVerifyAddress && (
                    <button
                      type="button"
                      onClick={function () {
                        setShowVerify(true);
                      }}
                      title="Verify this delivery address"
                      style={{
                        background: "#F57C00",
                        color: "white",
                        border: "none",
                        borderRadius: 6,
                        padding: "5px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        flexShrink: 0,
                      }}
                    >
                      ✓ Verify address
                    </button>
                  )}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#7c4a00",
                      background: "#FFF3E0",
                      border: "1px solid #FFB300",
                      borderRadius: 5,
                      padding: "4px 8px",
                    }}
                  >
                    ⚠ Delivery address pending Zephyr verification
                  </div>
                </div>
              )}
              {(function () {
                // Normalise: lowercase, collapse multiple spaces, strip trailing commas
                function normAddr(s) {
                  return (s || "").toLowerCase().replace(/\s+/g, " ").replace(/,\s*/g, ", ").trim();
                }
                const addr = normAddr(req.deliveryAddress);
                const matchesReg =
                  shelterRegAddrs.length > 0 &&
                  shelterRegAddrs.some(function (r) {
                    const reg = normAddr(r);
                    return reg === addr || addr.indexOf(reg) >= 0 || reg.indexOf(addr) >= 0;
                  });
                // matchesReg always wins — if address matches a registered address, never warn
                // even if the nonRegisteredAddress flag was set at submission time (pre-v118 format).
                // Also suppressed while the amber "pending Zephyr verification" message is shown
                // (deliveryAddressVerified === false) — we don't want two delivery warnings at once.
                const isUnreg =
                  req.deliveryAddressVerified !== false &&
                  !matchesReg &&
                  (req.nonRegisteredAddress || (shelterAddrsLoaded && shelterRegAddrs.length > 0));
                return isUnreg ? (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#C62828",
                      marginBottom: 6,
                      background: "#FFEBEE",
                      border: "1px solid #EF9A9A",
                      borderRadius: 5,
                      padding: "4px 8px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    ⚠ Unregistered address — verify with shelter
                  </div>
                ) : null;
              })()}
              <button
                onClick={function () {
                  setEditDelivery(req.deliveryAddress || "");
                  setShowAddressModal(true);
                }}
                disabled={lockEdits}
                title={
                  lockEdits
                    ? "Locked — a Stationery or Device item has been approved. A supervisor can still edit."
                    : "Edit delivery address"
                }
                style={{
                  background: lockEdits ? "#B0BEC5" : "#00897B",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "5px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: lockEdits ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                ✎ Edit Address
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Shelter notes */}
      <ShelterInfoBox
        shelterCode={req.shelterCode}
        shelterName={req.shelterName}
        user={user}
        lockEdits={lockEdits}
      />

      {/* Request summary */}
      <div
        style={{
          marginBottom: 0,
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${"#CFE0DE"}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: C.blue,
            color: "white",
            padding: "0 18px",
            height: 48,
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span>Request Summary</span>
          <button
            type="button"
            onClick={function () {
              setShowPeople(true);
            }}
            title="See everyone who has worked on this request"
            style={{
              background: "#E3F2FD",
              color: "#37474F",
              border: "1px solid #CFE0DE",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            People on this request
          </button>
        </div>
        <div
          style={{
            padding: "14px 16px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {repeatSummary && repeatSummary.count > 0 && (
            <div
              role="status"
              data-testid="repeat-summary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "#FFF8E1",
                border: `${repeatSummary.strong ? "2px solid" : "2px dashed"} #FFB300`,
                borderRadius: 6,
                padding: "6px 10px",
                marginBottom: 8,
                fontSize: 13,
                fontWeight: 700,
                color: "#8A4B00",
              }}
            >
              <span aria-hidden="true">⚠</span>
              <span>
                {`${repeatSummary.count} possible repeat${repeatSummary.count === 1 ? "" : "s"} — see ${repeatSummary.childIndexes
                  .map(function (ci) {
                    return `Child ${ci + 1}`;
                  })
                  .join(", ")}`}
              </span>
            </div>
          )}
          {feeTotal > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                padding: "4px 0",
                borderBottom: `1px solid ${"#CFE0DE"}`,
              }}
            >
              <span>School Costs & Stationery</span>
              <strong>{money(feeTotal)}</strong>
            </div>
          )}
          {slTotal > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                padding: "4px 0",
                borderBottom: `1px solid ${"#CFE0DE"}`,
              }}
            >
              <span>School Locker</span>
              <strong>{money(slTotal)}</strong>
            </div>
          )}
          {uniTotal > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                padding: "4px 0",
                borderBottom: `1px solid ${"#CFE0DE"}`,
              }}
            >
              <span>Other Uniforms</span>
              <strong>{money(uniTotal)}</strong>
            </div>
          )}
          {shoeTotal > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                padding: "4px 0",
                borderBottom: `1px solid ${"#CFE0DE"}`,
              }}
            >
              <span>Shoes</span>
              <strong>{money(shoeTotal)}</strong>
            </div>
          )}
          {hasLaptops && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                padding: "4px 0",
                borderBottom: `1px solid ${"#CFE0DE"}`,
                color: "#607D79",
              }}
            >
              <span>Device</span>
              {laptopTotal > 0 ? (
                <span style={{ fontWeight: 700, color: "#1A2E2B" }}>
                  {money(laptopTotal)} <span style={{ fontSize: 11, fontWeight: 400 }}>est.</span>
                </span>
              ) : (
                <span style={{ fontSize: 12 }}>see detail</span>
              )}
            </div>
          )}
          {grand > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 16,
                fontWeight: 700,
                color: "#00695C",
                paddingTop: 8,
                marginTop: 4,
              }}
            >
              <span>TOTAL</span>
              <span>{money(grand)}</span>
            </div>
          )}
        </div>
      </div>
      {showPeople && (
        <RequestPeopleModal
          requestId={req.id}
          refNo={req.refNo}
          onClose={function () {
            setShowPeople(false);
          }}
        />
      )}
      {showVerify && (
        <VerifyAddressModal
          onCancel={function () {
            setShowVerify(false);
          }}
          onConfirm={async function () {
            await onVerifyAddress();
            setShowVerify(false);
          }}
        />
      )}
    </div>
  );
}
